import type { RequestContext } from "../../../application/context/request-context.js";
import type { IntegrityReportId, InspectionId, RecommendationId } from "../../../shared/kernel/identifiers.js";
import type { IntegrityReport } from "../../../domain/report/integrity-report-domain.js";
import type { Recommendation } from "../../../domain/recommendation/recommendation-domain.js";
import type { IntegrityReportRepository, RecommendationRepository } from "../../../repositories/operational-repositories.js";
import { AppError, ErrorCode } from "../../../shared/errors/app-error.js";

export class InMemoryIntegrityReportRepository implements IntegrityReportRepository {
  private reports = new Map<string, IntegrityReport>();

  constructor() {
    try {
      const data = localStorage.getItem('inspect_reports');
      if (data) this.reports = new Map(JSON.parse(data));
    } catch (e) {
      console.warn("Failed to load reports from localStorage", e);
    }
  }

  private persist() {
    try {
      localStorage.setItem('inspect_reports', JSON.stringify(Array.from(this.reports.entries())));
    } catch (e) {
      console.warn("Failed to save reports to localStorage", e);
    }
  }

  private matchesClient(itemClientId: string, activeClientId: string): boolean {
    if (!itemClientId || !activeClientId) return true;
    return itemClientId.toUpperCase() === activeClientId.toUpperCase();
  }

  async findById(context: RequestContext, id: IntegrityReportId): Promise<IntegrityReport | null> {
    const item = this.reports.get(id);
    return (item && this.matchesClient(item.clientId, context.activeClientId)) ? item : null;
  }

  async findByInspection(context: RequestContext, inspectionId: InspectionId): Promise<IntegrityReport | null> {
    return Array.from(this.reports.values()).find(r => this.matchesClient(r.clientId, context.activeClientId) && r.inspectionId === inspectionId) || null;
  }

  async save(context: RequestContext, report: IntegrityReport, expectedVersion?: number): Promise<void> {
    this.reports.set(report.id, report);
    this.persist();
  }

  async delete(context: RequestContext, id: IntegrityReportId): Promise<void> {
    const item = this.reports.get(id);
    if (!item || !this.matchesClient(item.clientId, context.activeClientId)) {
      throw new AppError(ErrorCode.NotFound, `Report ${id} not found for client ${context.activeClientId}`, {
        safeMessage: 'Relatório não encontrado para exclusão neste cliente.'
      });
    }
    this.reports.delete(id);
    this.persist();
  }

  async listAll(context: RequestContext, options?: { projectId?: string | null }): Promise<IntegrityReport[]> {
    // IntegrityReport não possui projectId direto no domínio; o filtro por projeto
    // é aplicado via inspectionId/equipmentId na camada de domínio (computeDashboard).
    void options;
    return Array.from(this.reports.values()).filter(r =>
      this.matchesClient(r.clientId, context.activeClientId)
    );
  }
}

export class InMemoryRecommendationRepository implements RecommendationRepository {
  private recommendations = new Map<string, Recommendation>();

  constructor() {
    try {
      const data = localStorage.getItem('inspect_recommendations');
      if (data) this.recommendations = new Map(JSON.parse(data));
    } catch (e) {
      console.warn("Failed to load recommendations from localStorage", e);
    }
  }

  private persist() {
    try {
      localStorage.setItem('inspect_recommendations', JSON.stringify(Array.from(this.recommendations.entries())));
    } catch (e) {
      console.warn("Failed to save recommendations to localStorage", e);
    }
  }

  private matchesClient(itemClientId: string, activeClientId: string): boolean {
    if (!itemClientId || !activeClientId) return true;
    return itemClientId.toUpperCase() === activeClientId.toUpperCase();
  }

  async findById(context: RequestContext, id: RecommendationId): Promise<Recommendation | null> {
    const item = this.recommendations.get(id);
    return (item && this.matchesClient(item.clientId, context.activeClientId)) ? item : null;
  }

  async findByIntegrityReport(context: RequestContext, reportId: IntegrityReportId): Promise<readonly Recommendation[]> {
    return Array.from(this.recommendations.values()).filter(r => this.matchesClient(r.clientId, context.activeClientId) && r.integrityReportId === reportId);
  }

  async findByInspection(context: RequestContext, inspectionId: InspectionId): Promise<readonly Recommendation[]> {
    return Array.from(this.recommendations.values()).filter(r => this.matchesClient(r.clientId, context.activeClientId) && r.inspectionId === inspectionId);
  }

  async listAll(context: RequestContext, options?: { projectId?: string | null }): Promise<readonly Recommendation[]> {
    return Array.from(this.recommendations.values()).filter(r =>
      this.matchesClient(r.clientId, context.activeClientId) &&
      (!options?.projectId || r.projectId === options.projectId)
    );
  }

  async findByEquipment(context: RequestContext, equipmentId: string): Promise<readonly Recommendation[]> {
    return Array.from(this.recommendations.values()).filter(r => this.matchesClient(r.clientId, context.activeClientId) && r.equipmentId === equipmentId);
  }

  async save(context: RequestContext, recommendation: Recommendation, expectedVersion?: number): Promise<void> {
    this.recommendations.set(recommendation.id, recommendation);
    this.persist();
  }

  async delete(context: RequestContext, id: RecommendationId): Promise<void> {
    this.recommendations.delete(id);
    this.persist();
  }

  async deleteByIntegrityReport(context: RequestContext, reportId: IntegrityReportId): Promise<void> {
    for (const [id, rec] of this.recommendations.entries()) {
      if (rec.integrityReportId === reportId) {
        this.recommendations.delete(id);
      }
    }
    this.persist();
  }
}
